import KatalLogger, {Level} from "@amzn/katal-logger";
import DataHandler from "../handler/DataHandler";
import Constants from "../constants/Constants";
import DeviceDetailsHelper from "../helper/DeviceDetailsHelper";
import { v4 as uuidv4 } from 'uuid';
import {getAwsRegion} from "./BaseURLUtils";
import {getS3Client} from "../client/S3Client";
import AWS from "aws-sdk";

/**
 * Generate CloudWatch embedded metric format log which will be converted to CloudWatch metric
 * @param {*} nameSpace
 * @param {*} metricName
 * @param {*} metricValue
 * @param {*} metricUnit
 * @param {*} dimensions
 * @returns
 */
export const createEMFObject = (
    nameSpace,
    metricNameLabel,
    metricValue,
    metricUnit,
    dimensions = {},
) => {
    return {
        _aws: {
            Timestamp: Date.now(),
            CloudWatchMetrics: [
                {
                    Namespace: nameSpace,
                    Dimensions: [Object.keys(dimensions)],
                    Metrics: [
                        {
                            Name: metricNameLabel,
                            Unit: metricUnit
                        },
                    ],
                },
            ],
        },
        ...dimensions,
        [metricNameLabel]: metricValue,
    };
};

const Logger = {
    secureLog : null,
    currentIdToken : '',
    lastTokenUpdate : Date.now(),  //Tracks the last token update timestamp
    tokenExpirationTime : 45 * 60 * 1000, //45 minutes in milliseconds
    username : "",
    dummyUserAuthCode : "",
    signInConfiguration: "",
    katalLoggerConfig: {},
    async signInUser(signInConfig, username, password) {
        const cognito = new AWS.CognitoIdentityServiceProvider();
        const params = {
            AuthFlow: 'USER_PASSWORD_AUTH', // Bypasses SRP protocol
            ClientId: signInConfig.cognitoUserPoolWebClientId, // Cognito Web Client ID
            AuthParameters: {
                USERNAME: username,
                PASSWORD: password
            }
        };
        return new Promise((resolve, reject) => {
            cognito.initiateAuth(params, (err, data) => {
                if (err) {
                    reject(err); // Reject if authentication fails
                } else {
                    // On success, get the JWT token
                    const idToken = data.AuthenticationResult.IdToken;
                    resolve(idToken); // Return the JWT token
                }
            });
        });
    },
    async setLogger(loggerURL, stage, signInConfig, batchSize = 25, loggerConfig = {}) {
        let newIdToken = '';
        this.signInConfiguration = signInConfig;
        this.lastTokenUpdate = Date.now();  //Tracks the last token update timestamp
        this.tokenExpirationTime = 45 * 60 * 1000; //45 minutes in milliseconds
        try {
            let awsRegion =  signInConfig.awsRegion || getAwsRegion(signInConfig.region);
            const s3 = getS3Client(awsRegion, signInConfig.cognitoIdentityPoolId);
            const params = {
                Bucket: signInConfig.bucketName,
                Key: signInConfig.s3FileURI,
            };
            //Fetch guest user credentials from S3
            const data = await s3.getObject(params).promise()
            const json = JSON.parse(data.Body.toString());
            this.username = json.username
            this.dummyUserAuthCode = json.password
            //SignIn user
            AWS.config.update({
                region: awsRegion,
                credentials: new AWS.CognitoIdentityCredentials({
                    IdentityPoolId: signInConfig.cognitoIdentityPoolId, // Cognito Identity Pool ID
                }),
            });
            newIdToken = await this.signInUser(signInConfig, this.username, this.dummyUserAuthCode)
            this.currentIdToken = newIdToken;
        } catch (error) {
            console.error("Error retrieving session for user:", error);
        }
        this.katalLoggerConfig = {
            ...this.katalLoggerConfig,
            decodeStackTrace : true,
            recordMetrics: false,
            maxLogLineSize:200000,
            logThreshold: Level.WARN,
            url: loggerURL,
            maxLogsPerBatch: batchSize,
            logToConsole: (stage === Constants.Stage.BETA|| stage === Constants.Stage.GAMMA),
            headers: {
                'Accept-Language': '*',
                Authorization: newIdToken,
            },
            context: {
                userId: DataHandler.getUserId(),
                country: DataHandler.getCountry(),
                deviceIMEI: DeviceDetailsHelper.getDeviceIMEI(),
                stationCode: DataHandler.getStationCode(),
                deviceChromeVersion: DeviceDetailsHelper.getChromeVersion(),
                dolphinSessionId: uuidv4(),
                version: window.location.pathname.split("/")[1]
            },
            ...loggerConfig
        };
        this.secureLog = new KatalLogger(this.katalLoggerConfig);
        this.secureLog.addErrorListener();
    },
    async getLogger() {
        return this.secureLog;
    },
    async getLoggerWithRefreshToken() {
        const currentTime = Date.now();
        const elapsedTime = currentTime - this.lastTokenUpdate;
        // If 45 minutes have passed since the last token update, refresh the token
        if (elapsedTime >= this.tokenExpirationTime && this.currentIdToken) {
            let newIdToken = "";
            try {
                AWS.config.update({
                    region: this.signInConfiguration.awsRegion || getAwsRegion(this.signInConfiguration.region),
                    credentials: new AWS.CognitoIdentityCredentials({
                        IdentityPoolId: this.signInConfiguration.cognitoIdentityPoolId, // Cognito Identity Pool ID
                    }),
                });
                newIdToken = await this.signInUser(this.signInConfiguration, this.username, this.dummyUserAuthCode)
                this.currentIdToken = newIdToken;
                this.lastTokenUpdate = currentTime;
            } catch (error) {
                console.error("Error retrieving session for user:", error);
            }
            this.katalLoggerConfig = {
                ...this.katalLoggerConfig,
                headers: {
                    Authorization: newIdToken,
                },
            };
            this.secureLog = new KatalLogger(this.katalLoggerConfig);
            this.secureLog.addErrorListener();
        }
        return this.secureLog;
    },
    async _handleLog(level, message, ...contexts) {
        const secureLog = await this.getLoggerWithRefreshToken();
        if (secureLog) {
            if (contexts.length > 0) {
                return secureLog[level](message, ...contexts);  // e.g., logger.info(message, context1, context2)
            } else {
                return secureLog[level](message);  // e.g., logger.info(message)
            }
        } else {
            console.error("Logger is not initialized", message);
        }
    },
    log: {
        info: async function(message, ...contexts) {
            return this.parent._handleLog('info', message, ...contexts);
        },
        debug: async function(message, ...contexts) {
            return this.parent._handleLog('debug', message, ...contexts);
        },
        warn: async function(message, ...contexts) {
            return this.parent._handleLog('warn', message, ...contexts);
        },
        error: async function(message, ...contexts) {
            return this.parent._handleLog('error', message, ...contexts);
        },
        fatal: async function(message, ...contexts) {
            return this.parent._handleLog('fatal', message, ...contexts);
        },
        parent: null // This will be set after object creation
    }
};

// Initialize and export
Logger.log.parent = Logger;
export default Logger;